import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { agregarCapaRadar } from '../../utils/capaRadar';
import { agregarCapaSatelite } from '../../utils/capaSatelite';
import { agregarCapaCiclones } from '../../utils/capaCiclones';
import { getCiclones, condicionDe, rumboDe, type Ciclon } from '../../services/weather';
import { useClima } from '../../hooks/useClima';
import { useSettings } from '../../context/SettingsContext';

/**
 * Mapa del clima — separado a propósito del mapa de sensores.
 *
 * Al principio las capas de clima se pusieron encima de TODOS los mapas de
 * la plataforma. Se sentía práctico y resultó lo contrario: el mapa de
 * sensores existe para ver dónde está cada equipo y cómo va, y una capa de
 * lluvia animada encima compite con eso justo cuando más se necesita leerlo.
 * Son dos preguntas distintas —"cómo van mis equipos" y "qué tiempo viene"—
 * y cada una merece su mapa.
 *
 * Aquí viven las tres capas de clima, y ninguna en el otro lado.
 */

interface Props {
  lat: number;
  lng: number;
  /** Nombre del lugar, para la ficha. */
  nombre?: string;
  zoom?: number;
  alto?: number | string;
}

/** Cada cuánto se refresca la ficha de condiciones. */
const REFRESCO_MS = 10 * 60 * 1000;

export default function WeatherMap({ lat, lng, nombre, zoom = 7, alto = '100%' }: Props) {
  const { t } = useTranslation();
  const { formatTemp } = useSettings();
  const contenedorRef = useRef<HTMLDivElement>(null);
  const mapaRef = useRef<L.Map | null>(null);
  const [ciclones, setCiclones] = useState<Ciclon[]>([]);
  const { clima } = useClima(lat, lng);

  // ── Mapa base ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!contenedorRef.current || mapaRef.current) return;
    let mapa: L.Map;
    try {
      mapa = L.map(contenedorRef.current, { maxZoom: 21 }).setView([lat, lng], zoom);
    } catch (err) {
      console.warn('[Clima] Leaflet no arrancó:', err);
      return;
    }
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      { attribution: 'ESRI', maxZoom: 21, maxNativeZoom: 19 },
    ).addTo(mapa);
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
      { opacity: 0.7, maxZoom: 21, maxNativeZoom: 19 },
    ).addTo(mapa);
    mapaRef.current = mapa;
    return () => { mapa.remove(); mapaRef.current = null; };
    // Sólo al montar: mover la vista después es trabajo del efecto de abajo,
    // que sí sabe distinguir un cambio real de ubicación de un renderizado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recentrar SÓLO cuando la ubicación cambia de verdad. Sin la firma, cada
  // renderizado arrancaría al usuario de donde estuviera mirando — el mismo
  // bug que ya se corrigió en el mapa de sensores.
  const firma = `${lat},${lng}`;
  useEffect(() => {
    mapaRef.current?.setView([lat, lng], zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma]);

  // ── Capas de clima ─────────────────────────────────────────────────
  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;
    const quitarRadar = agregarCapaRadar(mapa, t('weather.radarLayer'), h => t('weather.radarTime', { time: h }));
    const quitarSat = agregarCapaSatelite(mapa, t('weather.satelliteLayer'), h => t('weather.satelliteTime', { time: h }));
    return () => { quitarRadar(); quitarSat(); };
  }, [t]);

  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;
    return agregarCapaCiclones(mapa, ciclones, {
      etiqueta: t('weather.stormsLayer'),
      conoAviso: t('weather.coneWarning'),
      ficha: c =>
        `${c.nombre} · ${t(`weather.class.${c.clasificacion}`, { defaultValue: c.clasificacion })}` +
        `\n${t('weather.sustainedWind')}: ${Math.round(c.vientoNudos * 1.852)} km/h` +
        (c.avanceNudos !== null
          ? `\n${t('weather.moving')}: ${rumboDe(c.rumboGrados ?? undefined)} · ${Math.round(c.avanceNudos * 1.852)} km/h`
          : ''),
    });
  }, [ciclones, t]);

  // ── Datos ──────────────────────────────────────────────────────────
  useEffect(() => {
    let vivo = true;
    const traer = () => { getCiclones().then(d => { if (vivo) setCiclones(d); }).catch(() => {}); };
    traer();
    const reloj = setInterval(traer, REFRESCO_MS);
    return () => { vivo = false; clearInterval(reloj); };
  }, []);

  const ahora = clima?.current;
  const cond = condicionDe(ahora?.weather_code);

  return (
    <div style={{ position: 'relative', height: alto, width: '100%' }}>
      <div ref={contenedorRef} style={{ height: '100%', width: '100%' }} />

      {/* Ficha de condiciones. Va sobre el mapa y no al lado para que el
          mapa se quede con todo el ancho — el dato es corto. */}
      {ahora && (
        <div
          className="glass"
          style={{
            position: 'absolute', top: 10, left: 10, zIndex: 500,
            background: 'var(--white)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '10px 14px', minWidth: 172,
            boxShadow: '0 2px 8px rgba(0,0,0,.12)', pointerEvents: 'none',
          }}
        >
          {nombre && (
            <div style={{ fontSize: 11, color: 'var(--gray)', marginBottom: 4, fontWeight: 600 }}>
              {nombre}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 24 }}>{cond.icono}</span>
            <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--dark)' }}>
              {ahora.temperature_2m !== undefined ? formatTemp(ahora.temperature_2m) : '—'}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--dark)', marginTop: 2 }}>
            {t(`weather.condition.${cond.clave}`, { defaultValue: '' })}
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 6, lineHeight: 1.7 }}>
            <div>
              💨 {t('weather.wind')} {rumboDe(ahora.wind_direction_10m)}{' '}
              {ahora.wind_speed_10m !== undefined ? `${Math.round(ahora.wind_speed_10m)} km/h` : '—'}
            </div>
            {ahora.wind_gusts_10m !== undefined && (
              <div>
                🌬 {t('weather.gusts')} {Math.round(ahora.wind_gusts_10m)} km/h
              </div>
            )}
            {ahora.relative_humidity_2m !== undefined && (
              <div>💧 {t('weather.humidity')} {Math.round(ahora.relative_humidity_2m)}%</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
